import { Form, reduxForm } from "redux-form";
import type { InjectedFormProps } from "redux-form";
import Button from "components/atoms/Button";
import ReduxFormFields from "components/molecules/ReduxFormFields";
import InputRedux from "components/molecules/InputRedux";
import PhoneInputRedux from "components/molecules/PhoneInputRedux";
import type { ReduxFormField } from "components/molecules/ReduxFormFields";
import { date, phone, required, requiredPhone } from "utils/validate.util";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SubjectIcon from "@mui/icons-material/Subject";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import {
  Box,
  Paper,
  Typography,
  Container,
  Grid,
  Divider
} from "@mui/material";
import EventIcon from '@mui/icons-material/Event'; 
import SelectRedux from "components/molecules/SelectRedux";
import { useTranslation } from "react-i18next";
import theme from "theme";
import { Event } from "@mui/icons-material";
import DatePickerRedux from "components/molecules/DatePickerRedux";

const AddEventReduxForm = ({ handleSubmit }: InjectedFormProps) => {
  const { t } = useTranslation();

  const fields: ReduxFormField[] = [
    {
      name: "first_name",
      label: t("name *"),
      component: InputRedux,
      validate: [required],
      cellProps: { xs: 12, sm: 6 },
      StartIcon: PersonIcon,
    },
    {
      name: "email",
      label: t("email *"),
      component: InputRedux,
      validate: [required],
      cellProps: { xs: 12, sm: 6 },
      StartIcon: EmailIcon,
    },
    {
      name: "phone",
      label: t("phoneNumber *"),
      component: PhoneInputRedux,
      validate: [requiredPhone, phone],
      cellProps: { xs: 12, sm: 6 },
    },
    {
      name: "event_type",
      label: t("eventType *"),
      component: SelectRedux,
      // StartIcon: SourceOutlinedIcon,
      StartIconProps: { color: "#939399" },
      validate: [required],
      cellProps: { xs: 12, sm: 6 },
      SelectProps: {
        customLabelStyles:false,
        options: [
          { label: t("corporate_event"), value: "corporate_event" },
          { label: t("airport_transfer"), value: "airport_transfer" },
          { label: t("wedding"), value: "wedding" },
          { label: t("concert"), value: "concert" },
          { label: t("conference"), value: "conference" },
          { label: t("exhibition"), value: "exhibition" },
          { label: t("other"), value: "other" },
        ],
      },
    },
    {
      name: "city",
      label: t("city *"),
      component: InputRedux,
      validate: [required],
      cellProps: { xs: 12, sm: 6 },
      StartIcon: LocationCityIcon,
    },
    {
      name: "number_of_vehicles",
      label: t("numberOfVehicles *"),
      component: InputRedux,
      validate: [required],
      cellProps: { xs: 12, sm: 6 },
      StartIcon: DirectionsCarIcon,
    },
    {
      name: "start_date",
      label: t("startDate *"),
      component: DatePickerRedux,
      validate: [required, date],
      cellProps: { xs: 12, sm: 6 },
      StartIcon: EventIcon,
      DatePickerProps: { customLabelStyles:false ,value:{date:null,error:false} } as any, // Disable custom label styles for this field
    },
    {
      name: "end_date",
      label: t("endDate *"),
      component: DatePickerRedux,
      validate: [required,date],
      cellProps: { xs: 12, sm: 6 },
      StartIcon: EventIcon,
        DatePickerProps: { customLabelStyles: false,value:{date:null,error:false} } as any, // Disable custom label styles for this field
    },
    {
      name: "event_description",
      label: t("comments"),
      component: InputRedux,
      cellProps: { xs: 12, sm: 6 },
      StartIcon: SubjectIcon,
    },
  ];

  return (
    <Container
      maxWidth="md"
      sx={{
        minHeight: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: { xs: 4, md: 2 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
          borderRadius: 3,
          width: "100%",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(106, 27, 154, 0.08)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "6px",
            background: "linear-gradient(90deg, #6a1b9a 0%, #9c27b0 100%)",
            zIndex: 10,
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "100%" },
            p: { xs: 3, sm: 5 },
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translateY(-50%)",
              width: { xs: 0, md: "40px" },
              height: { xs: 0, md: "80%" },
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
              zIndex: 5,
              display: { xs: "none", md: "block" },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: "50px",
                background: theme.palette.secondary.contrastText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                boxShadow: "0 4px 10px rgba(106, 27, 154, 0.2)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Event
                  sx={{ fontSize: 30, color: "white", paddingTop: "5px" }}
                />
              </Box>
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  color: "#520A76",
                  fontWeight: 800,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                  lineHeight: 1.2,
                }}
              >
                {t("eventTransportation")}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#7B2FA0",
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", md: "1rem" },
                  mt: 0.5,
                }}
              >
                {t("requestAQuote")}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: "#666",
              mb: 4,
              maxWidth: "600px",
              lineHeight: 1.6,
            }}
          >
            {t("eventTransportationSubtitle")}
          </Typography>

          <Form onSubmit={handleSubmit}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color: theme.palette.secondary.contrastText,
                  mb: 3,
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  "&::before": {
                    content: '""',
                    display: "inline-block",
                    width: "4px",
                    height: "20px",
                    backgroundColor: "#6a1b9a",
                    marginRight: "10px",
                    borderRadius: "4px",
                  },
                }}
              >
                {t("personalInformation")}
              </Typography>
              <Grid
                container
                spacing={2.5}
                sx={{
                  ml: { xs: 0, sm: 1, md: "5px" },
                  mt: { xs: 1, md: "4px" },
                  pr: { xs: 2, sm: 3, md: 0 },
                }}
              >
                <ReduxFormFields fields={fields.slice(0, 4)} />
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color: theme.palette.secondary.contrastText,
                  mb: 3,
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  "&::before": {
                    content: '""',
                    display: "inline-block",
                    width: "4px",
                    height: "20px",
                    backgroundColor: "#6a1b9a",
                    marginRight: "10px",
                    borderRadius: "4px",
                  },
                }}
              >
                {t("eventDetails")}
              </Typography>
              <Grid
                container
                spacing={2.5}
                sx={{
                  ml: { xs: 0, sm: 1, md: "5px" }, // margin-left responsive
                  mt: { xs: 1, md: "4px" }, // margin-top responsive
                  pr: { xs: 2, sm: 3, md: 0 }, // padding-right increases on small screens
                }}
              >
                <ReduxFormFields fields={fields.slice(4)} />
              </Grid>
            </Box>

            <Divider
              sx={{
                my: 4,
                borderColor: "#f0f0f0",
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 3.5,
                  py: 1.2,
                  borderRadius: 3.5,
                  fontWeight: 600,
                  color: "white",
                  fontSize: "1rem",
                  textTransform: "none",
                  minWidth: "180px",
                }}
              >
                {t("getAQuote")}
              </Button>
            </Box>
          </Form>
        </Box>
      </Paper>
    </Container>
  );
};

export default reduxForm({
  form: "AddEventForm",
  destroyOnUnmount: true,
})(AddEventReduxForm);
